import { useCallback, useEffect, useState } from 'react';
import {
  FolderArchive, Download, FileCheck2, Eye, AlertTriangle,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import Ayuda from '../ui/Ayuda';
import { fechaLarga, ATAJOS_PERIODO, periodoDe } from '../ui/calidad';
import { useAuth } from '../lib/contextoAuth';
import { Cargando } from '../ui/EstadoDePantalla';

// PAQUETE DE EVIDENCIA · Fase 6 del módulo de Calidad.
//
// QUÉ CONTESTA ESTA PANTALLA. La primera frase de toda auditoría de
// cliente, la que hoy cuesta una tarde de alguien abriendo pestañas:
//
//     «Deme todo lo de calidad de enero a marzo.»
//
// CUATRO DECISIONES DE USUARIO CERO QUE GOBIERNAN ESTO:
//
//   1. **Se ve ANTES de armarlo.** «Ver qué llevaría» dice, sección por
//      sección, qué va dentro — sin generar nada. Descubrir que el
//      trimestre trae cero certificados cuando ya se lo mandaste al
//      auditor es tarde.
//   2. **Las fechas se eligen con un botón**, no tecleando. «El
//      trimestre pasado» es como se dice en voz alta; 2026-01-01 es como
//      lo escribe una máquina. Los campos siguen ahí para quien quiera
//      un periodo raro.
//   3. **Se dice qué es cada sección en su renglón**, y las vacías
//      también aparecen, con su cuenta en cero. Una sección que
//      desaparece se lee como un dato que falta.
//   4. **Un paquete generado no se borra**, y se avisa antes de armarlo,
//      no después.

function Cifra({ n, que }) {
  return (
    <span className={n === 0 ? 'text-gray-400' : 'font-semibold text-gray-900'}>
      {n} {que}
    </span>
  );
}

// ---------------------------------------------------------------------
// Lo que llevaría, por sección
// ---------------------------------------------------------------------
function Secciones({ secciones, archivos, renglones, delPeriodo }) {
  return (
    <div className="border border-gray-200 rounded-xl bg-white divide-y divide-gray-100">
      {secciones.map((s) => (
        <div key={s.clave} className="p-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className={s.cuantas === 0 ? 'text-gray-500' : 'font-medium text-gray-900'}>
              {s.titulo}
            </div>
            {s.cuantas > 0 && s.con_archivo < s.cuantas && (
              <div className="text-xs text-gray-500">
                {s.con_archivo === 0
                  ? 'Van en el índice: no son archivos, son renglones.'
                  : `${s.con_archivo} con archivo adjunto; el resto va en el índice.`}
              </div>
            )}
          </div>
          <Cifra n={s.cuantas} que={s.cuantas === 1 ? 'cosa' : 'cosas'} />
        </div>
      ))}
      <div className="p-3 text-sm text-gray-700 bg-gray-50 rounded-b-xl">
        En total <strong>{archivos}</strong> archivo(s) y{' '}
        <strong>{renglones}</strong> renglón(es).
        {delPeriodo === 0 && (
          <span className="block text-amber-800 mt-1">
            En ese periodo no pasó nada: ni material recibido con certificado,
            ni inspecciones firmadas, ni no conformidades. Lo que se ve arriba
            son los documentos y las calibraciones vigentes hoy, que van en
            todos los paquetes.
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// La pantalla
// ---------------------------------------------------------------------
export default function PaqueteEvidencia() {
  const { puede } = useAuth();
  const [periodo, setPeriodo] = useState(() => periodoDe('trimestre'));
  const [previa, setPrevia] = useState(null);
  const [mirando, setMirando] = useState(false);
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  const [armando, setArmando] = useState(false);

  const cargar = useCallback(() => {
    api.get('/paquetes')
      .then((d) => setLista(d.paquetes))
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.'));
  }, []);

  useEffect(cargar, [cargar]);

  const cambiar = (campo) => (e) => {
    setPeriodo((a) => ({ ...a, [campo]: e.target.value }));
    setPrevia(null);
    setHecho(null);
  };

  const alReves = periodo.desde && periodo.hasta && periodo.desde > periodo.hasta;
  const falta = !periodo.desde ? 'la fecha de inicio'
    : !periodo.hasta ? 'la fecha del final'
    : alReves ? 'que las fechas estén en orden'
    : null;

  async function verQueLlevaria() {
    setError(null);
    setMirando(true);
    try {
      setPrevia(await api.get(
        `/paquetes/vista-previa?desde=${periodo.desde}&hasta=${periodo.hasta}`));
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo consultar.');
    } finally {
      setMirando(false);
    }
  }

  async function armar() {
    setError(null);
    setArmando(true);
    try {
      const d = await api.post('/paquetes', periodo);
      setHecho(d);
      setPrevia(null);
      cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo armar.');
    } finally {
      setArmando(false);
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold text-gray-900">Carpeta para la auditoría</h1>
        <p className="text-sm text-gray-600">
          Todo lo de calidad de un periodo en un solo archivo, con un índice
          que lleva la huella de cada documento. Es lo que se le entrega a un
          auditor o a un cliente.
        </p>
      </header>

      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      {hecho && (
        <div role="status" className="border border-green-200 bg-green-50 rounded-xl p-4 space-y-2">
          <div className="font-semibold text-green-900">{hecho.mensaje}</div>
          {hecho.faltantes?.length > 0 && (
            <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-2 flex gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                {hecho.faltantes.length} archivo(s) están registrados pero no
                se encontraron en disco. El índice los lista con su huella.
                Avísale a quien cuide el respaldo antes de entregar esto.
              </span>
            </div>
          )}
          <a href={hecho.url}
             className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold">
            <Download size={16} /> Descargar el paquete
          </a>
          <p className="text-xs text-gray-600">
            Huella del contenido: <code className="break-all">{hecho.huella}</code>
          </p>
        </div>
      )}

      {puede('calidad.paquete_evidencia') && (
        <section className="border border-gray-200 rounded-xl p-4 bg-white space-y-4">
          <h2 className="font-semibold text-gray-900">Armar uno nuevo</h2>

          {/* LAS FECHAS SE ELIGEN CON UN BOTÓN. «El trimestre pasado» es
              como se dice en voz alta; una caja de fecha vacía obliga a
              hacer cuentas de calendario con el auditor esperando. */}
          <div className="flex flex-wrap gap-2">
            {ATAJOS_PERIODO.map(([clave, etiqueta]) => (
              <button
                key={clave} type="button"
                onClick={() => { setPeriodo(periodoDe(clave)); setPrevia(null); setHecho(null); }}
                className="border border-gray-300 bg-white text-gray-800 rounded-lg px-3 py-1.5 text-sm font-medium hover:border-gray-400"
              >
                {etiqueta}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">Desde</span>
              <input type="date" value={periodo.desde} onChange={cambiar('desde')}
                     className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-base" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">Hasta</span>
              <input type="date" value={periodo.hasta} onChange={cambiar('hasta')}
                     className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-base" />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={verQueLlevaria} disabled={!!falta || mirando}
                    className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-800 rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
              <Eye size={16} /> {mirando ? 'Mirando…' : 'Ver qué llevaría'}
            </button>
            <Ayuda id="paquete_evidencia.huella" />
            <button type="button" onClick={armar} disabled={!!falta || armando}
                    className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
              <FolderArchive size={16} /> {armando ? 'Armando…' : 'Armar el paquete'}
            </button>
            {falta && <span className="text-sm text-gray-500">Falta {falta}.</span>}
          </div>

          {previa && (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-900">
                Del {fechaLarga(previa.desde)} al {fechaLarga(previa.hasta)}
              </div>
              {previa.aviso && (
                <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {previa.aviso}
                </div>
              )}
              <Secciones
                secciones={previa.secciones}
                archivos={previa.archivos}
                renglones={previa.renglones}
                delPeriodo={previa.del_periodo}
              />
              <p className="text-xs text-gray-500">
                Sin paquete generado. Pulsa «Armar el paquete»
                cuando el periodo sea el bueno.
              </p>
            </div>
          )}

        </section>
      )}

      <section className="space-y-2">
        <h2 className="font-semibold text-gray-900">Los que ya se entregaron</h2>

        {lista === null && <Cargando filas={4} />}

        {lista !== null && lista.length === 0 && (
          <EstadoVacio
            icono={FolderArchive}
            titulo="Todavía no has armado ningún paquete"
            descripcion={
              'Un paquete junta en un solo archivo los certificados de tus '
              + 'proveedores, tus documentos vigentes, las inspecciones que se '
              + 'firmaron, las no conformidades y las calibraciones de un '
              + 'periodo. Con un índice que lleva la huella de cada uno, para '
              + 'que quien lo reciba pueda comprobar que no se tocó nada.'
            }
          />
        )}

        {lista !== null && lista.length > 0 && (
          <div className="space-y-2">
            {lista.map((p) => (
              <div key={p.id} className="border border-gray-200 rounded-xl p-3 bg-white">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900">
                      Paquete {p.folio} · del {fechaLarga(p.desde)} al {fechaLarga(p.hasta)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {p.piezas} archivo(s) · {(p.bytes / 1048576).toFixed(1)} MB
                      {p.sucursal ? ` · ${p.sucursal}` : ''}
                    </div>
                    <div className="text-sm text-gray-600">
                      {p.generado_por
                        ? `Lo armó ${p.generado_por}.`
                        : 'Lo armó alguien que ya no está en el sistema.'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <FileCheck2 size={12} />
                      <code className="break-all">{p.huella}</code>
                    </div>
                  </div>
                  <a href={p.url}
                     className="shrink-0 inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-800 rounded-lg px-3 py-2 text-sm font-semibold">
                    <Download size={16} /> Descargar
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
